<template>
  <div class="pl-legal">
    <header class="pl-legal-header">
      <router-link to="/" class="pl-legal-brand">
        <img src="@/assets/ProfitLedgex.png" alt="ProfitLedgex" class="pl-legal-logo" />
        <span>ProfitLedgex</span>
      </router-link>
      <a href="#" class="pl-legal-back" @click.prevent="goBack">Back to sign up</a>
    </header>

    <main class="pl-legal-content">
      <h1>Terms &amp; Conditions</h1>
      <p class="pl-legal-meta">Version {{ version }} &middot; Last updated {{ lastUpdated }}</p>

      <p>
        These Terms &amp; Conditions ("Terms") govern your access to and use of ProfitLedgex
        (the "Service"), operated by ProfitLedgex ("we", "us", "our"). By creating an account,
        you agree to be bound by these Terms. If you do not agree, do not create an account or
        use the Service.
      </p>

      <h2>1. The Service</h2>
      <p>
        ProfitLedgex is a business management tool that helps small businesses, freelancers,
        and service providers manage invoicing, customers, products, transactions, and related
        financial records, including GST-compliant invoicing for use in India.
      </p>

      <h2>2. Eligibility &amp; Account Registration</h2>
      <p>
        You must be at least 18 years old and legally capable of entering into a binding
        contract to use the Service. When you sign up, you agree to provide accurate, current,
        and complete information (including your name, email address, and phone number), and
        to keep this information up to date. You are responsible for maintaining the
        confidentiality of your account credentials and for all activity that occurs under
        your account.
      </p>

      <h2>3. Free Trial &amp; Subscription</h2>
      <p>
        New accounts receive a 15-day free trial. Continued use of the Service after the trial
        period ends requires payment of the applicable subscription fee. We will make
        reasonable efforts to notify you before your trial or subscription expires. Failure to
        renew may result in restricted access to your account and data until payment is made.
      </p>

      <h2>4. Team Members</h2>
      <p>
        The Service allows an account holder ("Owner") to invite additional individuals ("Team
        Members") to access the Owner's account under the Owner's subscription. Team Member
        access is licensed solely for individuals who work for or with the Owner's own business
        (such as employees, contractors, or the Owner's accountant) — it may not be used to
        provide unrelated third parties, including operators of a separate business, with access
        to the Service under the Owner's subscription in place of that party purchasing their
        own subscription. We reserve the right to suspend or terminate Team Member access, or
        require a separate subscription, if we determine an account is being used to provide
        Service access to an unrelated business. The Owner is responsible for all activity
        conducted by their Team Members and for ensuring Team Member use complies with these
        Terms.
      </p>

      <h2>5. Financial &amp; Bank Account Information</h2>
      <p>
        To generate invoices and support payment collection, the Service allows you to store
        business and bank account details (such as account number, IFSC code, and UPI details)
        that are displayed on invoices you issue to your own customers. This information is
        provided by you, is under your control, and is used solely to populate documents you
        generate and to operate the Service on your behalf. We do not initiate transactions
        against, or move funds from, any bank account you store. See our
        <router-link to="/privacy">Privacy Policy</router-link> for details on how this data is
        stored and protected.
      </p>

      <h2>6. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service for any unlawful purpose or in violation of any applicable law or regulation;</li>
        <li>Upload or store data you do not have the right to store, or that infringes another party's rights;</li>
        <li>Attempt to gain unauthorized access to any account, system, or network related to the Service;</li>
        <li>Interfere with or disrupt the integrity or performance of the Service;</li>
        <li>Use the Service to generate fraudulent invoices or misrepresent transactions;</li>
        <li>Invite a Team Member on behalf of a business unrelated to the Owner's own, as described in Section 4.</li>
      </ul>

      <h2>7. Your Data</h2>
      <p>
        You retain ownership of the business, customer, and financial data you enter into the
        Service. You are responsible for the accuracy of that data, including amounts, taxes,
        and compliance with applicable tax law (such as GST) for invoices you issue. We act as
        a data processor for the records you create and do not claim ownership of your
        business data.
      </p>

      <h2>8. Suspension &amp; Termination</h2>
      <p>
        We may suspend or terminate your account if you breach these Terms, engage in
        fraudulent or unlawful activity, or fail to pay applicable fees. You may stop using the
        Service and request deletion of your account at any time by contacting support; see our
        Privacy Policy for how account and data deletion requests are handled.
      </p>

      <h2>9. Disclaimers</h2>
      <p>
        The Service is provided "as is" and "as available" without warranties of any kind,
        express or implied. We do not warrant that the Service will be uninterrupted, secure,
        or error-free, or that it constitutes tax, legal, or financial advice. You are
        responsible for verifying that invoices and records generated through the Service meet
        your legal and regulatory obligations.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, ProfitLedgex and its operators shall not be
        liable for any indirect, incidental, special, or consequential damages, or for any loss
        of profits, data, or business arising from your use of, or inability to use, the
        Service. Our total liability for any claim arising from these Terms or the Service
        shall not exceed the amount you paid us in the twelve (12) months preceding the claim.
      </p>

      <h2>11. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will be reflected by an
        updated "Last updated" date and version number above. Continued use of the Service
        after changes take effect constitutes acceptance of the revised Terms.
      </p>

      <h2>12. Governing Law</h2>
      <p>
        These Terms are governed by the laws of India, without regard to conflict-of-law
        principles. Any disputes arising from these Terms or the Service shall be subject to
        the exclusive jurisdiction of the courts located in India.
      </p>

      <h2>13. Contact Us</h2>
      <p>
        If you have questions about these Terms, please contact us through the
        <router-link to="/support">Support</router-link> section of the app.
      </p>
    </main>
  </div>
</template>

<script>
import { TERMS_VERSION, TERMS_LAST_UPDATED } from '@/utils/legal';

export default {
  name: 'TermsPage',
  data: () => ({
    version: TERMS_VERSION,
    lastUpdated: TERMS_LAST_UPDATED,
  }),
  methods: {
    goBack() {
      // Opened via target="_blank" from signup — close this tab so the
      // signup form (with whatever the user already typed) is untouched,
      // rather than navigating in-tab and losing it. Falls back to a normal
      // navigation if the tab wasn't opened by script (e.g. visited directly
      // or opened by the browser in a way close() can't affect).
      window.close();
      if (!window.closed) {
        this.$router.push('/signup');
      }
    },
  },
};
</script>

<style scoped>
.pl-legal {
  min-height: 100vh;
  background: #f4f6f9;
  font-family: 'Manrope', system-ui, sans-serif;
  color: #0e2433;
}

.pl-legal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 32px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8ee;
  position: sticky;
  top: 0;
  z-index: 5;
}

.pl-legal-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: #0b1e2d;
  font-weight: 800;
  font-size: 17px;
}

.pl-legal-logo {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  object-fit: cover;
}

.pl-legal-back {
  color: #0a6cc0;
  font-weight: 700;
  font-size: 13.5px;
  text-decoration: none;
}
.pl-legal-back:hover { text-decoration: underline; }

.pl-legal-content {
  max-width: 760px;
  margin: 0 auto;
  padding: 40px 24px 80px;
  line-height: 1.65;
}

.pl-legal-content h1 {
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 6px;
}

.pl-legal-meta {
  color: #64747f;
  font-size: 13px;
  margin: 0 0 28px;
}

.pl-legal-content h2 {
  font-size: 18px;
  font-weight: 700;
  margin: 30px 0 10px;
  color: #0b1e2d;
}

.pl-legal-content p {
  font-size: 14.5px;
  color: #374955;
  margin: 0 0 14px;
}

.pl-legal-content ul {
  margin: 0 0 14px;
  padding-left: 22px;
}

.pl-legal-content li {
  font-size: 14.5px;
  color: #374955;
  margin-bottom: 6px;
}

.pl-legal-content a {
  color: #0a6cc0;
  font-weight: 600;
  text-decoration: none;
}
.pl-legal-content a:hover { text-decoration: underline; }
</style>
